import { useState, useEffect } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Card, CardContent } from "../../../../ui/card";
import { Checkbox } from "../../../../ui/checkbox";
import { Badge } from "../../../../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../../../ui/tabs";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Loader2, Search, RefreshCw, Sparkles, Copy, Check, MessageCircle } from "lucide-react";
import { useToast } from "../../../../../hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { OutletMultiSelect } from "./OutletMultiSelect";

type Platform = "all" | "google_maps" | "instagram" | "tiktok" | "facebook" | "tripadvisor" | "epicreviews";

interface ReviewInboxProps {
  preselectedOutlets?: string[];
  supabaseoutletIDs?: string[],
}

export const ReviewInbox = ({ preselectedOutlets, supabaseoutletIDs }: ReviewInboxProps) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<any[]>([]);
  const [outlets, setOutlets] = useState<any[]>([]);
  const [platformCounts, setPlatformCounts] = useState<Record<string, number>>({});
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>("all");
  const [selectedRating, setSelectedRating] = useState<string>("all");
  const [selectedOutlets, setSelectedOutlets] = useState<string[]>(["all"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReviews, setSelectedReviews] = useState<Set<string>>(new Set());
  const [generatingReply, setGeneratingReply] = useState<string | null>(null);
  const [generatedReplies, setGeneratedReplies] = useState<Record<string, string>>({});
  const [copiedReply, setCopiedReply] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadOutlets();
  }, []);

  useEffect(() => {
    if (preselectedOutlets) {
      setSelectedOutlets(preselectedOutlets);
    }
  }, [preselectedOutlets]);

  useEffect(() => {
    loadReviews();
    loadPlatformCounts();
  }, [selectedPlatform, selectedRating, selectedOutlets]);

  const loadOutlets = async () => {
    try {
      const { data, error } = await supabase
        .from("outlets")
        .select("*").in('id', supabaseoutletIDs)
        .order("outlet_name");

      if (error) throw error;
      setOutlets(data || []);
    } catch (error) {
      console.error("Error loading outlets:", error);
    }
  };

  const loadReviews = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("reviews")
        .select("*")
        .order("posted_at", { ascending: false })
        .limit(200); // Increased limit to show more reviews

      if (selectedPlatform !== "all") {
        query = query.eq("platform", selectedPlatform as any);
      }

      if (selectedRating !== "all") {
        query = query.eq("rating", parseInt(selectedRating));
      }

      const isAllSelected = !selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs);
      }

      const { data, error } = await query;
      if (error) throw error;

      setReviews(data || []);
    } catch (error) {
      console.error("Error loading reviews:", error);
      toast({
        title: "Error loading reviews",
        description: "Failed to load reviews. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPlatformCounts = async () => {
    try {
      let query = supabase
        .from("reviews")
        .select("platform");

      const isAllSelected = !selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs)
      }

      const { data, error } = await query;
      if (error) throw error;

      const counts = (data || []).reduce((acc, review) => {
        acc[review.platform] = (acc[review.platform] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      setPlatformCounts(counts);
    } catch (error) {
      console.error("Error loading platform counts:", error);
    }
  };

  const filteredReviews = reviews.filter((review) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        review.author_name?.toLowerCase().includes(query) ||
        review.comment_text?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleToggleReview = (reviewId: string) => {
    const newSelected = new Set(selectedReviews);
    if (newSelected.has(reviewId)) {
      newSelected.delete(reviewId);
    } else {
      newSelected.add(reviewId);
    }
    setSelectedReviews(newSelected);
  };

  const handleMarkAsHandled = async () => {
    const reviewIds = Array.from(selectedReviews);
    if (reviewIds.length === 0) return;

    try {
      const { error } = await supabase
        .from("reviews")
        .update({ is_handled: true, handled_at: new Date().toISOString() })
        .in("id", reviewIds);

      if (error) throw error;

      toast({
        title: "Reviews marked as handled",
        description: `${reviewIds.length} review(s) updated`,
      });

      setSelectedReviews(new Set());
      loadReviews();
    } catch (error) {
      console.error("Error updating reviews:", error);
      toast({
        title: "Error",
        description: "Failed to update reviews",
        variant: "destructive",
      });
    }
  };

  const getPlatformBadgeColor = (platform: string) => {
    const colors: Record<string, string> = {
      google_maps: "bg-blue-500",
      instagram: "bg-purple-500",
      tiktok: "bg-pink-500",
      facebook: "bg-blue-600",
      tripadvisor: "bg-green-500",
      epicreviews: "bg-purple-600",
    };
    return colors[platform] || "bg-gray-500";
  };

  const getSentimentColor = (sentiment: string) => {
    const colors: Record<string, string> = {
      positive: "bg-green-500/10 text-green-700 border-green-200",
      neutral: "bg-yellow-500/10 text-yellow-700 border-yellow-200",
      negative: "bg-red-500/10 text-red-700 border-red-200",
    };
    return colors[sentiment] || "bg-gray-500/10 text-gray-700 border-gray-200";
  };

  const getAvatarUrl = (avatar: string | null) => {
    if (!avatar) return null;
    
    // Try to parse as JSON (TripAdvisor format)
    try {
      const parsed = JSON.parse(avatar);
      return parsed.image || null;
    } catch {
      // If not JSON, return as-is (direct URL)
      return avatar;
    }
  };

  const handleGenerateReply = async (review: any) => {
    setGeneratingReply(review.id);
    try {
      const { data, error } = await supabase.functions.invoke("generate-reply", {
        body: {
          reviewText: review.comment_text,
          rating: review.rating,
          platform: review.platform,
          authorName: review.author_name,
        },
      });

      if (error) throw error;

      if (data?.reply) {
        setGeneratedReplies((prev) => ({
          ...prev,
          [review.id]: data.reply,
        }));
        toast({
          title: "Reply generated",
          description: "AI has generated a professional response.",
        });
      }
    } catch (error: any) {
      console.error("Error generating reply:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate reply",
        variant: "destructive",
      });
    } finally {
      setGeneratingReply(null);
    }
  };

  const handleCopyReply = async (reviewId: string) => {
    const reply = generatedReplies[reviewId];
    if (reply) {
      await navigator.clipboard.writeText(reply);
      setCopiedReply(reviewId);
      toast({
        title: "Copied to clipboard",
        description: "Reply has been copied.",
      });
      setTimeout(() => setCopiedReply(null), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Review Inbox</h2>
        <Button variant="outline" size="sm" onClick={loadReviews}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Platform Tabs */}
      <Tabs value={selectedPlatform} onValueChange={(v) => setSelectedPlatform(v as Platform)}>
        <TabsList className="w-full justify-start overflow-x-auto review-tab-list">
          <TabsTrigger value="all" className="gap-2">
            All <Badge variant="secondary">{Object.values(platformCounts).reduce((sum, count) => sum + count, 0)}</Badge>
          </TabsTrigger>
          <TabsTrigger value="google_maps" className="gap-2">
            🌍 Google Maps <Badge variant="secondary">{platformCounts.google_maps || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="instagram" className="gap-2">
            🟪 Instagram <Badge variant="secondary">{platformCounts.instagram || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="tiktok" className="gap-2">
            🎵 TikTok <Badge variant="secondary">{platformCounts.tiktok || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="facebook" className="gap-2">
            🟦 Facebook <Badge variant="secondary">{platformCounts.facebook || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="tripadvisor" className="gap-2">
            🟩 TripAdvisor <Badge variant="secondary">{platformCounts.tripadvisor || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="epicreviews" className="gap-2">
            💫 EpicReview <Badge variant="secondary">{platformCounts.epicreviews || 0}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <OutletMultiSelect
          outlets={outlets}
          selectedOutlets={selectedOutlets}
          onChange={setSelectedOutlets}
        />

        <Select value={selectedRating} onValueChange={setSelectedRating}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Ratings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ratings</SelectItem>
            <SelectItem value="5">5 ★★★★★</SelectItem>
            <SelectItem value="4">4 ★★★★</SelectItem>
            <SelectItem value="3">3 ★★★</SelectItem>
            <SelectItem value="2">2 ★★</SelectItem>
            <SelectItem value="1">1 ★</SelectItem>
          </SelectContent>
        </Select>

        {selectedReviews.size > 0 && (
          <Button variant="secondary" onClick={handleMarkAsHandled}>
            Mark {selectedReviews.size} as Handled
          </Button>
        )}
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No reviews found for this channel yet.</p>
            <Button variant="outline" className="mt-4" onClick={loadReviews}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Reviews
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <Card key={review.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-4">
                  <Checkbox
                    checked={selectedReviews.has(review.id)}
                    onCheckedChange={() => handleToggleReview(review.id)}
                  />

                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {getAvatarUrl(review.author_avatar) && (
                          <img
                            src={getAvatarUrl(review.author_avatar)!}
                            alt={review.author_name}
                            className="h-10 w-10 rounded-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <p className="font-medium">{review.author_name || "Anonymous"}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge className={`${getPlatformBadgeColor(review.platform)} text-white border-0`}>
                              {review.platform.replace("_", " ")}
                            </Badge>
                            {review.rating && (
                              <span className="text-yellow-500">{"★".repeat(review.rating)}</span>
                            )}
                            {review.platform === "epicreviews" && (review as any).table_number && (
                              <Badge variant="outline" className="border-purple-300 text-purple-700">
                                Table {(review as any).table_number}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {review.sentiment && (
                          <Badge variant="outline" className={getSentimentColor(review.sentiment)}>
                            {review.sentiment}
                          </Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {review.posted_at ? formatDistanceToNow(new Date(review.posted_at), { addSuffix: true }) : 'Date unknown'}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm">{review.comment_text}</p>

                    {/* Display photos for EpicReviews */}
                    {review.platform === "epicreviews" && review.photos && review.photos.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto">
                        {review.photos.map((photo: string, index: number) => (
                          <img
                            key={index}
                            src={photo}
                            alt={`Review photo ${index + 1}`}
                            className="h-24 w-24 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => window.open(photo, '_blank')}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* WhatsApp button for EpicReviews only */}
                    {review.platform === "epicreviews" && review.customer_phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => window.open(`https://wa.me/${review.customer_phone.replace(/[^0-9]/g, '')}`, '_blank')}
                      >
                        <MessageCircle className="h-4 w-4 text-green-600" />
                        Contact on WhatsApp
                      </Button>
                    )}

                    {!generatedReplies[review.id] ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateReply(review)}
                        disabled={generatingReply === review.id}
                      >
                        {generatingReply === review.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            Generate AI Reply
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="space-y-2 border rounded-lg p-3 bg-muted/50">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">AI Generated Reply</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyReply(review.id)}
                          >
                            {copiedReply === review.id ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <Textarea
                          value={generatedReplies[review.id]}
                          onChange={(e) =>
                            setGeneratedReplies((prev) => ({
                              ...prev,
                              [review.id]: e.target.value,
                            }))
                          }
                          className="text-sm min-h-[80px]"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateReply(review)}
                          disabled={generatingReply === review.id}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Regenerate
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
